import {useCallback, useEffect, useState} from "react";
import {Alert, Button, CardHeader, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useLocation, useSearchParams} from "react-router-dom";
import {PageContainer} from "../../components/common/page-container";
import {ContentContainer} from "../../components/common/content-container";
import useOrderQueue from "../../components/orders/use-order-queue";
import OrderQueueView from "../../components/orders/order-queue-view";
import {OrderShipmentsLink} from "../../components/orders/order-shipments";
import useOrderDecision from "../../components/orders/use-order-decision";
import OrderDetailsDialog from "../../dialog/order-details-dialog";
import GlowingMessage from "../../errors/glowing-message";
import useTransientMessage from "../../errors/use-transient-message";
import useNotificationOrder from "../../notifications/use-notification-order";
import NotificationOrderFeedback from "../../notifications/notification-order-feedback";
import {OrderConfirmationLink} from "../../components/orders/order-confirmation-entry";
import axios from "../../config/axios-config";

export default function MyOrdersPharmacy({navOpen}) {
    const {t, i18n} = useTranslation(), location = useLocation(), [params, setParams] = useSearchParams(), queue = useOrderQueue("pharmacy");
    const [selected, setSelected] = useState(null), [details, setDetails] = useState(false), [cancel, setCancel] = useState(null);
    const {message, messageKey, showMessage: setMessage, dismiss} = useTransientMessage();
    const decision = useOrderDecision(`${queue.identity}:${i18n.language}:${cancel?.orderId}`);
    const reset = useCallback(() => {setSelected(null); setDetails(false); setCancel(null);}, []);
    const openDetails = useCallback(order => {setSelected(order); setDetails(true);}, []);
    const openShipments = useCallback(order => {const next = new URLSearchParams(params); next.set("shipments", order.orderId); setParams(next);}, [params, setParams]);
    const openReview = useCallback(order => {
        const next = new URLSearchParams(params); next.set("approval", order.proposalId); setParams(next);
    }, [params, setParams]);
    const notificationOrder = useNotificationOrder(order => {
        setSelected({...order.distributor, orderNumber: order.orderNumber}); setDetails(true);
    }, reset);
    useEffect(() => {reset(); dismiss();}, [queue.identity, i18n.language, reset, dismiss]);
    useEffect(() => {if ((new URLSearchParams(location.search).has("approval") || new URLSearchParams(location.search).has("shipments"))) {reset();}}, [location.search]);
    const cancelOrder = async () => {
        if (await decision.submit(cancel, {status: "CANCELLED", rejectionReason: "CancelledByPharmacy", rejection: false, deliveryDate: null})) {
            setCancel(null); queue.refresh(); setMessage(t("OrderQueueDecisionSaved"));
        }
    };
    const reorder = async order => {
        try {
            if (!Array.isArray(order.items)) {throw new Error("Reorder unavailable");}
            for (const item of order.items) {await axios.post("/api/v1/carts", {productTenantId: item.productTenantId, count: item.quantity});}
            setMessage(t("ItemsAddedToBasket"));
        } catch (error) {setMessage(error.response?.data?.message || t("ErrorReordering"));}
    };
    return <>
        <GlowingMessage message={message} messageKey={messageKey} onClose={dismiss}/><OrderConfirmationLink/><OrderShipmentsLink/>
        <PageContainer navOpen={navOpen} sx={{flex: 1, minWidth: 0, width: "auto"}}>
            <NotificationOrderFeedback status={notificationOrder}/><CardHeader title={t("MyOrders")} subheader={t("MyOrdersTableDesc")}/>
            <ContentContainer elevation={2}><OrderQueueView queue={queue} pharmacy
                onDetails={openDetails} onShipments={openShipments} onCancel={setCancel} onReview={openReview}/>
            </ContentContainer>
        </PageContainer>
        {details && <OrderDetailsDialog open orderNumber={selected?.orderNumber} selectedDistributor={selected}
            onOpenRevision={proposalId => openReview({proposalId})}
            onClose={() => {setDetails(false); setSelected(null);}} onReorder={reorder}/>}
        <Dialog open={Boolean(cancel)} onClose={() => {if (!decision.pending) {setCancel(null);}}} aria-labelledby="cancel-order-title" maxWidth="sm" fullWidth>
            <DialogTitle id="cancel-order-title">{t("CancelOrder")} {cancel?.orderNumber}</DialogTitle>
            <DialogContent><DialogContentText>{t("CancelOrderConfirmation")}</DialogContentText>
                {decision.error && <Alert severity="error">{t(decision.error)}</Alert>}
            </DialogContent>
            <DialogActions><Button disabled={decision.pending} onClick={() => setCancel(null)}>{t("Cancel")}</Button>
                <Button disabled={decision.pending} color="error" onClick={cancelOrder}>{t(decision.uncertain ? "Retry" : "Confirm")}</Button></DialogActions>
        </Dialog>
    </>;
}
